import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/PrivacyPolicyModal.scss';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('privacy');

  if (!isOpen) return null;

  return (
    <div className="privacy-policy-modal">
      <div className="privacy-policy-overlay" onClick={onClose}>
        <div className="privacy-policy-content" onClick={e => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>
            <span>&times;</span>
          </button>
          
          <div className="privacy-policy-header">
            <h1>{t('title')}</h1>
            <p className="date">{t('lastUpdated')}</p>
            <p className="date">{t('effectiveDate')}</p>
          </div>
          
          <div className="privacy-policy-body">
            {/* 1. Introduction */}
            <section className="intro">
              <h2>{t('sections.intro.title')}</h2>
              <p>{t('sections.intro.welcome')}</p>
              <p>{t('sections.intro.consent')}</p>
            </section>
            
            {/* 2. Information We Collect */}
            <section className="information-collect">
              <h2>{t('sections.informationWeCollect.title')}</h2>
              <p>{t('sections.informationWeCollect.intro')}</p>
              
              <div className="subsection">
                <h3>{t('sections.informationWeCollect.personalData.title')}</h3>
                <p>{t('sections.informationWeCollect.personalData.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('sections.informationWeCollect.sensitiveInput.title')}</h3>
                <p>{t('sections.informationWeCollect.sensitiveInput.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('sections.informationWeCollect.usageData.title')}</h3>
                <p>{t('sections.informationWeCollect.usageData.content')}</p>
                <ul>
                  {Array.isArray(t('sections.informationWeCollect.usageData.items', { returnObjects: true })) && 
                    t('sections.informationWeCollect.usageData.items', { returnObjects: true }).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))
                  }
                </ul>
              </div>
            </section>
            
            {/* 3. How We Use Your Information */}
            <section className="how-we-use">
              <h2>{t('sections.howWeUse.title')}</h2>
              <p>{t('sections.howWeUse.intro')}</p>
              
              {Array.isArray(t('sections.howWeUse.items', { returnObjects: true })) && 
                t('sections.howWeUse.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
            </section>
            
            {/* 4. How We Disclose Information */}
            <section className="disclosure">
              <h2>{t('sections.disclosure.title')}</h2>
              <p>{t('sections.disclosure.intro')}</p>
              
              <div className="subsection">
                <h3>{t('sections.disclosure.serviceProviders.title')}</h3>
                <p>{t('sections.disclosure.serviceProviders.content')}</p>
                <ul>
                  {Array.isArray(t('sections.disclosure.serviceProviders.items', { returnObjects: true })) && 
                    t('sections.disclosure.serviceProviders.items', { returnObjects: true }).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))
                  }
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('sections.disclosure.emergency.title')}</h3>
                <p>{t('sections.disclosure.emergency.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('sections.disclosure.aggregatedData.title')}</h3>
                <p>{t('sections.disclosure.aggregatedData.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('sections.disclosure.businessTransfers.title')}</h3>
                <p>{t('sections.disclosure.businessTransfers.content')}</p>
              </div>
              
              <p>{t('sections.disclosure.noSale.content')}</p>
            </section>
            
            {/* 5. Legal Basis */}
            <section className="legal-basis">
              <h2>{t('sections.legalBasis.title')}</h2>
              <p>{t('sections.legalBasis.content')}</p>
              
              {Array.isArray(t('sections.legalBasis.items', { returnObjects: true })) && 
                t('sections.legalBasis.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
            </section>
            
            {/* 6. Data Retention */}
            <section className="data-retention">
              <h2>{t('sections.dataRetention.title')}</h2>
              <p>{t('sections.dataRetention.content')}</p>
              
              {Array.isArray(t('sections.dataRetention.items', { returnObjects: true })) && 
                t('sections.dataRetention.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
              
              <p>{t('sections.dataRetention.deletion')}</p>
            </section>
            
            {/* 7. Data Security */}
            <section className="security">
              <h2>{t('sections.security.title')}</h2>
              <p>{t('sections.security.content')}</p>
              
              {Array.isArray(t('sections.security.items', { returnObjects: true })) && 
                t('sections.security.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
              
              <p>{t('sections.security.disclaimer')}</p>
            </section>
            
            {/* 8. Your Rights */}
            <section className="rights">
              <h2>{t('sections.rights.title')}</h2>
              <p>{t('sections.rights.content')}</p>
              
              {Array.isArray(t('sections.rights.items', { returnObjects: true })) && 
                t('sections.rights.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
              
              <p>{t('sections.rights.contact')}</p>
              <p>{t('sections.rights.complaint')}</p>
            </section>
            
            {/* 9. Children's Privacy */}
            <section className="children">
              <h2>{t('sections.children.title')}</h2>
              <p>{t('sections.children.content')}</p>
              <p>{t('sections.children.teens')}</p>
              <p>{t('sections.children.parentContact')}</p>
            </section>
            
            {/* 10. International Data Transfers */}
            <section className="international">
              <h2>{t('sections.international.title')}</h2>
              <p>{t('sections.international.content')}</p>
              <p>{t('sections.international.consent')}</p>
              <p>{t('sections.international.future')}</p>
            </section>
            
            {/* 11. Third-Party Links */}
            <section className="third-party">
              <h2>{t('sections.thirdParty.title')}</h2>
              <p>{t('sections.thirdParty.content')}</p>
              <p>{t('sections.thirdParty.socialMedia')}</p>
            </section>
            
            {/* 12. Updates to Privacy Policy */}
            <section className="updates">
              <h2>{t('sections.updates.title')}</h2>
              <p>{t('sections.updates.content')}</p>
              <p>{t('sections.updates.consent')}</p>
            </section>
            
            {/* 13. Contact Us */}
            <section className="contact">
              <h2>{t('sections.contact.title')}</h2>
              <p>{t('sections.contact.content')}</p>
              <p className="contact-info">{t('sections.contact.company')}</p>
              <p className="contact-info">{t('sections.contact.email')}</p>
              <p className="contact-info">{t('sections.contact.dpo')}</p>
              <p>{t('sections.contact.final')}</p>
              <p className="thanks">{t('sections.contact.thanks')}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal; 